import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Save, 
  X, 
  Calendar,
  Ship,
  Building2,
  User,
  FileText,
  AlertCircle
} from 'lucide-react';

const ModifierDrydoc = () => {
  const { id } = useParams();
  const [formData, setFormData] = useState({
    customer_id: '',
    customer_recipient_id: '',
    date_arrivee: '',
    date_depart: '',
    notes: ''
  });

  const [selectedClient, setSelectedClient] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);

  // Données de référence - normalement récupérées via API
  const clients = [
    {
      id: 1,
      name: 'CMA CGM',
      recipients: [
        { id: 1, label: 'CMA CGM Marseille', imo_number: 'IMO1234567', type: 'Ship' },
        { id: 4, label: 'CMA CGM Express', imo_number: 'IMO9876543', type: 'Ship' }
      ]
    },
    {
      id: 2,
      name: 'MSC',
      recipients: [
        { id: 2, label: 'MSC Terminal', imo_number: 'IMO7654321', type: 'Ship' }
      ]
    },
    {
      id: 3,
      name: 'Evergreen',
      recipients: [
        { id: 3, label: 'Evergreen Marseille', imo_number: 'IMO1112223', type: 'Ship' }
      ]
    },
    {
      id: 4,
      name: 'COSCO',
      recipients: [
        { id: 5, label: 'COSCO Shipping', imo_number: 'IMO5555666', type: 'Ship' }
      ]
    },
    {
      id: 5,
      name: 'Hapag-Lloyd',
      recipients: [
        { id: 6, label: 'Hapag Express', imo_number: 'IMO7777888', type: 'Ship' }
      ]
    }
  ];

  // Données de test des drydocs
  const drydocs = [
    {
      id: 1,
      customer_id: 1,
      customer_name: 'CMA CGM',
      customer_recipient_id: 1,
      customer_recipient_name: 'CMA CGM Marseille',
      imo_number: 'IMO1234567',
      date_arrivee: '2024-02-01',
      date_depart: '2024-02-15',
      notes: 'Maintenance moteur principal et révision générale',
      status: 'En cours'
    },
    {
      id: 2,
      customer_id: 2,
      customer_name: 'MSC',
      customer_recipient_id: 2,
      customer_recipient_name: 'MSC Terminal',
      imo_number: 'IMO7654321',
      date_arrivee: '2024-02-10',
      date_depart: '2024-02-25',
      notes: 'Réparation coque et peinture',
      status: 'En cours'
    },
    {
      id: 4,
      customer_id: 1,
      customer_name: 'CMA CGM',
      customer_recipient_id: 1,
      customer_recipient_name: 'CMA CGM Marseille',
      imo_number: 'IMO9876543',
      date_arrivee: '2024-02-20',
      date_depart: '2024-03-05',
      notes: 'Rénovation des cabines et espaces communs',
      status: 'Planifié'
    }
  ];

  // Charger les données du drydoc
  useEffect(() => {
    const drydoc = drydocs.find(d => d.id == id);
    if (drydoc) {
      setFormData({
        customer_id: drydoc.customer_id.toString(),
        customer_recipient_id: drydoc.customer_recipient_id.toString(),
        date_arrivee: drydoc.date_arrivee,
        date_depart: drydoc.date_depart,
        notes: drydoc.notes
      });
      
      const client = clients.find(c => c.id == drydoc.customer_id);
      setSelectedClient(client);
    }
    setLoading(false);
  }, [id]);

  // Effect pour mettre à jour les destinataires disponibles quand le client change
  useEffect(() => {
    if (formData.customer_id) {
      const client = clients.find(c => c.id == formData.customer_id);
      setSelectedClient(client);
      // Reset du recipient si le client change et que le recipient n'est pas compatible
      if (selectedClient && selectedClient.id != formData.customer_id) {
        const newClient = clients.find(c => c.id == formData.customer_id);
        const recipientExists = newClient?.recipients.some(r => r.id == formData.customer_recipient_id);
        if (!recipientExists) {
          setFormData(prev => ({ ...prev, customer_recipient_id: '' }));
        }
      }
    } else {
      setSelectedClient(null);
      setFormData(prev => ({ ...prev, customer_recipient_id: '' }));
    }
  }, [formData.customer_id]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.customer_id) {
      newErrors.customer_id = 'Le client est obligatoire';
    }

    if (!formData.customer_recipient_id) {
      newErrors.customer_recipient_id = 'Le bateau est obligatoire';
    }

    if (!formData.date_arrivee) {
      newErrors.date_arrivee = 'La date d\'arrivée est obligatoire';
    }

    if (!formData.date_depart) {
      newErrors.date_depart = 'La date de départ est obligatoire';
    }

    if (formData.date_arrivee && formData.date_depart) {
      const dateArrivee = new Date(formData.date_arrivee);
      const dateDepart = new Date(formData.date_depart);
      
      if (dateDepart <= dateArrivee) {
        newErrors.date_depart = 'La date de départ doit être postérieure à la date d\'arrivée';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Ici on enverrait les données à l'API
      console.log('Données du drydoc modifié:', formData);
      
      // Simulation de la sauvegarde
      alert('Drydoc modifié avec succès !');
      
      // Redirection vers la liste des drydocs
      window.location.href = '/drydocs';
    }
  };

  const handleCancel = () => {
    if (window.confirm('Êtes-vous sûr de vouloir annuler ? Les modifications non sauvegardées seront perdues.')) {
      window.location.href = '/drydocs';
    }
  };

  const calculateDuration = () => {
    if (formData.date_arrivee && formData.date_depart) {
      const start = new Date(formData.date_arrivee);
      const end = new Date(formData.date_depart);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    }
    return 0;
  };

  const selectedRecipient = selectedClient?.recipients.find(r => r.id == formData.customer_recipient_id);
  const currentDrydoc = drydocs.find(d => d.id == id);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-port-gray-600">Chargement...</div>
      </div>
    );
  }

  if (!currentDrydoc) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">Drydoc non trouvé</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-port-gray-900">
            Modifier le Drydoc #{id}
          </h1>
          <p className="text-port-gray-600">
            Modifier les informations du bateau {currentDrydoc.customer_recipient_name} en cale sèche
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button 
            onClick={handleCancel}
            className="btn-secondary flex items-center space-x-2"
          >
            <X className="h-5 w-5" />
            <span>Annuler</span>
          </button>
          <button 
            onClick={handleSubmit}
            className="btn-primary flex items-center space-x-2"
            disabled={!formData.customer_id || !formData.customer_recipient_id || !formData.date_arrivee || !formData.date_depart}
          >
            <Save className="h-5 w-5" />
            <span>Enregistrer</span>
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Informations principales */}
        <div className="card p-6">
          <h2 className="text-xl font-semibold text-port-gray-900 mb-6 flex items-center">
            <Ship className="h-6 w-6 mr-2" />
            Informations principales
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Client */}
            <div>
              <label className="block text-sm font-medium text-port-gray-700 mb-2">
                Client *
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-port-gray-400" />
                <select
                  value={formData.customer_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_id: e.target.value }))}
                  className={`input-field pl-10 ${errors.customer_id ? 'border-red-300' : ''}`}
                >
                  <option value="">Sélectionner un client</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>
              {errors.customer_id && (
                <div className="mt-1 flex items-center text-red-600 text-sm">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.customer_id}
                </div>
              )}
            </div>

            {/* Bateau */}
            <div>
              <label className="block text-sm font-medium text-port-gray-700 mb-2">
                Bateau *
              </label>
              <div className="relative">
                <Ship className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-port-gray-400" />
                <select
                  value={formData.customer_recipient_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_recipient_id: e.target.value }))}
                  className={`input-field pl-10 ${errors.customer_recipient_id ? 'border-red-300' : ''}`}
                  disabled={!selectedClient}
                >
                  <option value="">
                    {selectedClient ? 'Sélectionner un bateau' : 'Sélectionnez d\'abord un client'}
                  </option>
                  {selectedClient?.recipients.map(recipient => (
                    <option key={recipient.id} value={recipient.id}>
                      {recipient.label} {recipient.imo_number && `(${recipient.imo_number})`}
                    </option>
                  ))}
                </select>
              </div>
              {errors.customer_recipient_id && (
                <div className="mt-1 flex items-center text-red-600 text-sm">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.customer_recipient_id}
                </div>
              )}
              {selectedRecipient && (
                <div className="mt-2 text-sm text-port-gray-600">
                  IMO: {selectedRecipient.imo_number}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Période de cale sèche */}
        <div className="card p-6">
          <h2 className="text-xl font-semibold text-port-gray-900 mb-6 flex items-center">
            <Calendar className="h-6 w-6 mr-2" />
            Période de cale sèche
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Date d'arrivée */}
            <div>
              <label className="block text-sm font-medium text-port-gray-700 mb-2">
                Date d'arrivée *
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-port-gray-400" />
                <input
                  type="date"
                  value={formData.date_arrivee}
                  onChange={(e) => setFormData(prev => ({ ...prev, date_arrivee: e.target.value }))}
                  className={`input-field pl-10 ${errors.date_arrivee ? 'border-red-300' : ''}`}
                />
              </div>
              {errors.date_arrivee && (
                <div className="mt-1 flex items-center text-red-600 text-sm">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.date_arrivee}
                </div>
              )}
            </div>

            {/* Date de départ */}
            <div>
              <label className="block text-sm font-medium text-port-gray-700 mb-2">
                Date de départ *
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-port-gray-400" />
                <input
                  type="date"
                  value={formData.date_depart}
                  onChange={(e) => setFormData(prev => ({ ...prev, date_depart: e.target.value }))}
                  className={`input-field pl-10 ${errors.date_depart ? 'border-red-300' : ''}`}
                  min={formData.date_arrivee}
                />
              </div>
              {errors.date_depart && (
                <div className="mt-1 flex items-center text-red-600 text-sm">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.date_depart}
                </div>
              )}
            </div>
          </div>

          {/* Durée calculée */}
          {formData.date_arrivee && formData.date_depart && calculateDuration() > 0 && (
            <div className="mt-4 p-4 bg-atl-blue-50 rounded-lg">
              <div className="flex items-center text-atl-blue-700">
                <Calendar className="h-5 w-5 mr-2" />
                <span className="font-medium">
                  Durée prévue: {calculateDuration()} jour{calculateDuration() > 1 ? 's' : ''}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="card p-6">
          <h2 className="text-xl font-semibold text-port-gray-900 mb-6 flex items-center">
            <FileText className="h-6 w-6 mr-2" />
            Notes et observations
          </h2>
          
          <div>
            <label className="block text-sm font-medium text-port-gray-700 mb-2">
              Description des travaux prévus
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Décrivez les travaux de maintenance, réparations ou modifications prévues pendant la cale sèche..."
              rows={4}
              className="input-field"
            />
            <div className="mt-1 text-sm text-port-gray-500">
              Décrivez les travaux qui seront effectués sur le bateau pendant la période de cale sèche.
            </div>
          </div>
        </div>

        {/* Récapitulatif */}
        {formData.customer_id && formData.customer_recipient_id && (
          <div className="card p-6 bg-port-gray-50">
            <h2 className="text-xl font-semibold text-port-gray-900 mb-4 flex items-center">
              <User className="h-6 w-6 mr-2" />
              Récapitulatif des modifications
            </h2>
            
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-port-gray-600">Client:</span>
                <span className="font-medium text-port-gray-900">
                  {clients.find(c => c.id == formData.customer_id)?.name}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-port-gray-600">Bateau:</span>
                <span className="font-medium text-port-gray-900">
                  {selectedRecipient?.label}
                </span>
              </div>
              
              {selectedRecipient?.imo_number && (
                <div className="flex justify-between">
                  <span className="text-port-gray-600">IMO:</span>
                  <span className="font-medium text-port-gray-900">
                    {selectedRecipient.imo_number}
                  </span>
                </div>
              )}
              
              {formData.date_arrivee && (
                <div className="flex justify-between">
                  <span className="text-port-gray-600">Arrivée:</span>
                  <span className="font-medium text-port-gray-900">
                    {new Date(formData.date_arrivee).toLocaleDateString('fr-FR')}
                  </span>
                </div>
              )}
              
              {formData.date_depart && (
                <div className="flex justify-between">
                  <span className="text-port-gray-600">Départ:</span>
                  <span className="font-medium text-port-gray-900">
                    {new Date(formData.date_depart).toLocaleDateString('fr-FR')}
                  </span>
                </div>
              )}
              
              {calculateDuration() > 0 && (
                <div className="flex justify-between">
                  <span className="text-port-gray-600">Durée:</span>
                  <span className="font-medium text-atl-blue-600">
                    {calculateDuration()} jour{calculateDuration() > 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default ModifierDrydoc;

